import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

// Main component
const InnovationAssessment = () => {
  // State for current step
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Navigate to next step
  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Navigate to previous step
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Calculate results when form is submitted
  const calculateResults = () => {
    // Culture questions (A1-A5)
    const cultureValues = [
      parseInt(formData.A1 || 0),
      parseInt(formData.A2 || 0),
      parseInt(formData.A3 || 0),
      parseInt(formData.A4 || 0),
      parseInt(formData.A5 || 0)
    ];
    
    // Process questions (A6-A9)
    const processValues = [
      parseInt(formData.A6 || 0),
      parseInt(formData.A7 || 0),
      parseInt(formData.A8 || 0),
      parseInt(formData.A9 || 0)
    ];
    
    // Tools questions (A10-A13)
    const toolsValues = [
      parseInt(formData.A10 || 0),
      parseInt(formData.A11 || 0),
      parseInt(formData.A12 || 0),
      parseInt(formData.A13 || 0)
    ];
    
    // Strategy questions (A14-A17)
    const strategyValues = [
      parseInt(formData.A14 || 0),
      parseInt(formData.A15 || 0),
      parseInt(formData.A16 || 0),
      parseInt(formData.A17 || 0)
    ];

    // Calculate averages (normalize to percentages)
    const cultureAvg = cultureValues.reduce((a, b) => a + b, 0) / (cultureValues.length * 4) * 100;
    const processAvg = processValues.reduce((a, b) => a + b, 0) / (processValues.length * 4) * 100;
    const toolsAvg = toolsValues.reduce((a, b) => a + b, 0) / (toolsValues.length * 4) * 100;
    const strategyAvg = strategyValues.reduce((a, b) => a + b, 0) / (strategyValues.length * 4) * 100;
    
    // Calculate overall score
    const overallScore = (cultureAvg + processAvg + toolsAvg + strategyAvg) / 4;
    
    // Generate result message
    let message = generateFeedbackMessage(cultureAvg, processAvg, toolsAvg, strategyAvg, overallScore);
    
    setResults({
      culture: cultureAvg,
      processes: processAvg,
      tools: toolsAvg,
      strategy: strategyAvg,
      overall: overallScore,
      message: message
    });
    
    setShowResults(true);
  };

  // Generate personalized feedback based on scores
  const generateFeedbackMessage = (culture, processes, tools, strategy, overall) => {
    let message = `# Innovation Maturity Assessment Results\n\n`;
    message += `Your organization's overall innovation maturity score is **${overall.toFixed(2)}%**.\n\n`;
    
    // Add category-specific feedback
    message += `## Breakdown by Category:\n\n`;
    
    // Culture feedback
    message += `### Culture (${culture.toFixed(2)}%)\n`;
    if (culture < 25) {
      message += `Your organization shows early signs of innovation culture. Consider focusing on building trust and encouraging risk-taking.\n\n`;
    } else if (culture < 50) {
      message += `Your innovation culture is developing. Work on empowering employees to contribute ideas and propose changes.\n\n`;
    } else if (culture < 75) {
      message += `Your organization has a good innovation culture foundation. Continue fostering collaboration and recognition for innovation efforts.\n\n`;
    } else {
      message += `Excellent innovation culture! Your organization values employees as intrapreneurs and encourages risk-taking and collaboration.\n\n`;
    }
    
    // Processes feedback
    message += `### Processes (${processes.toFixed(2)}%)\n`;
    if (processes < 25) {
      message += `Innovation processes are at an early stage. Consider implementing basic idea collection and dedicated innovation time.\n\n`;
    } else if (processes < 50) {
      message += `Your organization has basic innovation processes. Focus on developing more structured ideation techniques and idea campaigns.\n\n`;
    } else if (processes < 75) {
      message += `Good innovation processes in place. Consider expanding to more comprehensive innovation governance and targeted ideation.\n\n`;
    } else {
      message += `Excellent innovation processes! Your organization has comprehensive innovation governance, funding, and ideation techniques.\n\n`;
    }
    
    // Tools feedback
    message += `### Tools (${tools.toFixed(2)}%)\n`;
    if (tools < 25) {
      message += `Innovation tools usage is minimal. Consider introducing basic innovation training and idea capture systems.\n\n`;
    } else if (tools < 50) {
      message += `Basic innovation tools are in place. Look to expand training programs and formalize idea management systems.\n\n`;
    } else if (tools < 75) {
      message += `Good innovation tools infrastructure. Consider implementing a centralized innovation management platform.\n\n`;
    } else {
      message += `Excellent innovation tools! Your organization provides comprehensive training and uses advanced innovation management platforms.\n\n`;
    }
    
    // Strategy feedback
    message += `### Strategy (${strategy.toFixed(2)}%)\n`;
    if (strategy < 25) {
      message += `Innovation strategy is in early development. Consider aligning innovation efforts with departmental strategies.\n\n`;
    } else if (strategy < 50) {
      message += `Basic innovation strategy exists. Work on establishing leadership roles for innovation and measuring progress.\n\n`;
    } else if (strategy < 75) {
      message += `Good innovation strategy in place. Consider elevating innovation to C-suite importance and implementing ROI-based metrics.\n\n`;
    } else {
      message += `Excellent innovation strategy! Your organization has integrated innovation into corporate strategy with comprehensive metrics.\n\n`;
    }
    
    // Overall recommendations
    message += `## Next Steps\n\n`;
    if (overall < 25) {
      message += `Focus on building a foundation for innovation by:\n- Establishing trust and encouraging collaboration\n- Implementing basic idea collection processes\n- Providing introductory innovation training\n- Aligning innovation with departmental goals\n`;
    } else if (overall < 50) {
      message += `Strengthen your innovation capabilities by:\n- Empowering employees to take calculated risks\n- Formalizing innovation time and idea campaigns\n- Expanding innovation training programs\n- Establishing innovation leadership roles\n`;
    } else if (overall < 75) {
      message += `Build on your strong foundation by:\n- Developing a culture of intrapreneurship\n- Implementing comprehensive innovation governance\n- Adopting innovation management platforms\n- Elevating innovation to strategic importance\n`;
    } else {
      message += `Maintain your innovation excellence by:\n- Continuously evolving your innovation approaches\n- Engaging customers and partners in innovation\n- Measuring innovation ROI and impact\n- Integrating innovation into all aspects of strategy\n`;
    }
    
    return message;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    calculateResults();
  };

  // Form step definitions
  const steps = [
    {
      title: "Welcome!",
      content: (
        <div className="welcome-content">
          <p>It will take <strong>approximately 6 minutes</strong> to complete this innovation maturity assessment. You will be asked to assess your innovation <strong>culture, processes, tools, and strategy</strong> by answering 3-5 questions for each category.</p>
          <p>All <strong>responses are strictly confidential</strong> and data from this assessment is reported only in aggregate. Personal information will be encoded and will remain confidential.</p>
          <p>If you have questions at any time about the survey or the procedures, please contact us by email at <a href="mailto:hello@lusidea.com">hello@lusidea.com</a>.</p>
        </div>
      )
    },
    {
      title: "Culture (1/6)",
      content: (
        <div className="question-section">
          <div className="question-group">
            <div className="question">How do employees perceive themselves in your company?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A1" value="0" onChange={handleChange} checked={formData.A1 === "0"} />
                <span>As productivity units</span>
              </label>
              <label className="option">
                <input type="radio" name="A1" value="1" onChange={handleChange} checked={formData.A1 === "1"} />
                <span>Valued contributors to company success</span>
              </label>
              <label className="option">
                <input type="radio" name="A1" value="2" onChange={handleChange} checked={formData.A1 === "2"} />
                <span>Problem solvers</span>
              </label>
              <label className="option">
                <input type="radio" name="A1" value="3" onChange={handleChange} checked={formData.A1 === "3"} />
                <span>Leaders of change</span>
              </label>
              <label className="option">
                <input type="radio" name="A1" value="4" onChange={handleChange} checked={formData.A1 === "4"} />
                <span>Intrapreneurs</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">How often do you engage other people to help you do your job?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A2" value="0" onChange={handleChange} checked={formData.A2 === "0"} />
                <span>Other people are busy and I'm not allowed to engage them</span>
              </label>
              <label className="option">
                <input type="radio" name="A2" value="1" onChange={handleChange} checked={formData.A2 === "1"} />
                <span>Sometimes I engage other people, but it's frowned upon</span>
              </label>
              <label className="option">
                <input type="radio" name="A2" value="2" onChange={handleChange} checked={formData.A2 === "2"} />
                <span>I engage other people when needed, but I try to do everything myself</span>
              </label>
              <label className="option">
                <input type="radio" name="A2" value="3" onChange={handleChange} checked={formData.A2 === "3"} />
                <span>I'm encouraged to seek help and collaborate with other people and teams</span>
              </label>
              <label className="option">
                <input type="radio" name="A2" value="4" onChange={handleChange} checked={formData.A2 === "4"} />
                <span>It's mandatory for me to involve other people and teams to get better feedback and a fresh perspective</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">Do your colleagues trust each other and the management?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A3" value="0" onChange={handleChange} checked={formData.A3 === "0"} />
                <span>There is a complete lack of trust</span>
              </label>
              <label className="option">
                <input type="radio" name="A3" value="1" onChange={handleChange} checked={formData.A3 === "1"} />
                <span>There is some trust, but we are mostly careful not to expose ourselves</span>
              </label>
              <label className="option">
                <input type="radio" name="A3" value="2" onChange={handleChange} checked={formData.A3 === "2"} />
                <span>A mix of both. There are colleagues I trust, and there are ones I don't trust</span>
              </label>
              <label className="option">
                <input type="radio" name="A3" value="3" onChange={handleChange} checked={formData.A3 === "3"} />
                <span>I trust most of my colleagues and managers</span>
              </label>
              <label className="option">
                <input type="radio" name="A3" value="4" onChange={handleChange} checked={formData.A3 === "4"} />
                <span>I have absolute trust in each member of our team and our company</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">Are you allowed to take risks when performing your regular job?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A4" value="0" onChange={handleChange} checked={formData.A4 === "0"} />
                <span>I'm not allowed to take risks</span>
              </label>
              <label className="option">
                <input type="radio" name="A4" value="1" onChange={handleChange} checked={formData.A4 === "1"} />
                <span>Yes, but taking risks is discouraged</span>
              </label>
              <label className="option">
                <input type="radio" name="A4" value="2" onChange={handleChange} checked={formData.A4 === "2"} />
                <span>Taking risks is tolerated in my company</span>
              </label>
              <label className="option">
                <input type="radio" name="A4" value="3" onChange={handleChange} checked={formData.A4 === "3"} />
                <span>Taking risks is encouraged</span>
              </label>
              <label className="option">
                <input type="radio" name="A4" value="4" onChange={handleChange} checked={formData.A4 === "4"} />
                <span>We are required to take risks to find new and creative ways of solving our problems</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">Are you allowed to propose changes and adjustments to your business activities?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A5" value="0" onChange={handleChange} checked={formData.A5 === "0"} />
                <span>I have a highly standardized job and proposing changes is discouraged</span>
              </label>
              <label className="option">
                <input type="radio" name="A5" value="1" onChange={handleChange} checked={formData.A5 === "1"} />
                <span>I'm allowed to propose changes and improvements, but they are considered as an annoyance</span>
              </label>
              <label className="option">
                <input type="radio" name="A5" value="2" onChange={handleChange} checked={formData.A5 === "2"} />
                <span>New ideas are acknowledged, but often no action is taken</span>
              </label>
              <label className="option">
                <input type="radio" name="A5" value="3" onChange={handleChange} checked={formData.A5 === "3"} />
                <span>I'm encouraged to challenge the status quo</span>
              </label>
              <label className="option">
                <input type="radio" name="A5" value="4" onChange={handleChange} checked={formData.A5 === "4"} />
                <span>I'm stimulated to propose improvements and I get rewarded or recognized</span>
              </label>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Processes (2/6)",
      content: (
        <div className="question-section">
          <div className="question-group">
            <div className="question">Do you have a part of work time dedicated to innovation?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A6" value="0" onChange={handleChange} checked={formData.A6 === "0"} />
                <span>No, I'm not allowed to work on innovation topics during my working hours</span>
              </label>
              <label className="option">
                <input type="radio" name="A6" value="1" onChange={handleChange} checked={formData.A6 === "1"} />
                <span>Sometimes I can get time approved to work on innovation</span>
              </label>
              <label className="option">
                <input type="radio" name="A6" value="2" onChange={handleChange} checked={formData.A6 === "2"} />
                <span>Yes, I have dedicated time up to a certain number of hours</span>
              </label>
              <label className="option">
                <input type="radio" name="A6" value="3" onChange={handleChange} checked={formData.A6 === "3"} />
                <span>I get flexible hours and I don't need to ask approval to work more on innovation</span>
              </label>
              <label className="option">
                <input type="radio" name="A6" value="4" onChange={handleChange} checked={formData.A6 === "4"} />
                <span>I'm required to continuously dedicate time for innovation initiatives, sometimes even full time</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">Who is the main source of ideas in your company?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A7" value="0" onChange={handleChange} checked={formData.A7 === "0"} />
                <span>Nobody</span>
              </label>
              <label className="option">
                <input type="radio" name="A7" value="1" onChange={handleChange} checked={formData.A7 === "1"} />
                <span>A small group of senior management</span>
              </label>
              <label className="option">
                <input type="radio" name="A7" value="2" onChange={handleChange} checked={formData.A7 === "2"} />
                <span>Small groups within each department</span>
              </label>
              <label className="option">
                <input type="radio" name="A7" value="3" onChange={handleChange} checked={formData.A7 === "3"} />
                <span>All employees</span>
              </label>
              <label className="option">
                <input type="radio" name="A7" value="4" onChange={handleChange} checked={formData.A7 === "4"} />
                <span>All employees, but also our customers and partners</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">What does your innovation methodology include?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A8" value="0" onChange={handleChange} checked={formData.A8 === "0"} />
                <span>We don't have an innovation process or methodology</span>
              </label>
              <label className="option">
                <input type="radio" name="A8" value="1" onChange={handleChange} checked={formData.A8 === "1"} />
                <span>Recognition and rewards for innovation initiatives</span>
              </label>
              <label className="option">
                <input type="radio" name="A8" value="2" onChange={handleChange} checked={formData.A8 === "2"} />
                <span>Recognition, ideation and idea management</span>
              </label>
              <label className="option">
                <input type="radio" name="A8" value="3" onChange={handleChange} checked={formData.A8 === "3"} />
                <span>Recognition, idea management, innovation goals and achievements</span>
              </label>
              <label className="option">
                <input type="radio" name="A8" value="4" onChange={handleChange} checked={formData.A8 === "4"} />
                <span>Comprehensive innovation governance and funding</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">Which ideation techniques are used in your company?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A9" value="0" onChange={handleChange} checked={formData.A9 === "0"} />
                <span>None</span>
              </label>
              <label className="option">
                <input type="radio" name="A9" value="1" onChange={handleChange} checked={formData.A9 === "1"} />
                <span>General idea submission and collection</span>
              </label>
              <label className="option">
                <input type="radio" name="A9" value="2" onChange={handleChange} checked={formData.A9 === "2"} />
                <span>General idea campaigns</span>
              </label>
              <label className="option">
                <input type="radio" name="A9" value="3" onChange={handleChange} checked={formData.A9 === "3"} />
                <span>Targeted idea campaigns</span>
              </label>
              <label className="option">
                <input type="radio" name="A9" value="4" onChange={handleChange} checked={formData.A9 === "4"} />
                <span>Idea generation sessions for specific problems</span>
              </label>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Tools (3/6)",
      content: (
        <div className="question-section">
          <div className="question-group">
            <div className="question">Does your company provide innovation training?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A10" value="0" onChange={handleChange} checked={formData.A10 === "0"} />
                <span>No</span>
              </label>
              <label className="option">
                <input type="radio" name="A10" value="1" onChange={handleChange} checked={formData.A10 === "1"} />
                <span>Yes, to a small group of senior management</span>
              </label>
              <label className="option">
                <input type="radio" name="A10" value="2" onChange={handleChange} checked={formData.A10 === "2"} />
                <span>Yes, to innovation champions and ambassadors</span>
              </label>
              <label className="option">
                <input type="radio" name="A10" value="3" onChange={handleChange} checked={formData.A10 === "3"} />
                <span>Yes, to all employees</span>
              </label>
              <label className="option">
                <input type="radio" name="A10" value="4" onChange={handleChange} checked={formData.A10 === "4"} />
                <span>Yes, to all employees, customers and partners</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">What kind of innovation training does your company provide?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A11" value="0" onChange={handleChange} checked={formData.A11 === "0"} />
                <span>Nothing</span>
              </label>
              <label className="option">
                <input type="radio" name="A11" value="1" onChange={handleChange} checked={formData.A11 === "1"} />
                <span>Creativity and idea generation</span>
              </label>
              <label className="option">
                <input type="radio" name="A11" value="2" onChange={handleChange} checked={formData.A11 === "2"} />
                <span>Innovation processes</span>
              </label>
              <label className="option">
                <input type="radio" name="A11" value="3" onChange={handleChange} checked={formData.A11 === "3"} />
                <span>Innovation management</span>
              </label>
              <label className="option">
                <input type="radio" name="A11" value="4" onChange={handleChange} checked={formData.A11 === "4"} />
                <span>Entrepreneurship, creativity and governance</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">How are ideas captured?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A12" value="0" onChange={handleChange} checked={formData.A12 === "0"} />
                <span>Ideas are not captured</span>
              </label>
              <label className="option">
                <input type="radio" name="A12" value="1" onChange={handleChange} checked={formData.A12 === "1"} />
                <span>We use various channels (verbal, email, etc.) to communicate ideas to department managers</span>
              </label>
              <label className="option">
                <input type="radio" name="A12" value="2" onChange={handleChange} checked={formData.A12 === "2"} />
                <span>We use various channels (verbal, email, etc.) to communicate ideas to innovation champions</span>
              </label>
              <label className="option">
                <input type="radio" name="A12" value="3" onChange={handleChange} checked={formData.A12 === "3"} />
                <span>We send ideas to a centralized idea database</span>
              </label>
              <label className="option">
                <input type="radio" name="A12" value="4" onChange={handleChange} checked={formData.A12 === "4"} />
                <span>We use an innovation management platform</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">How are ideas managed, evaluated and developed in your company?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A13" value="0" onChange={handleChange} checked={formData.A13 === "0"} />
                <span>We don't have idea management in our company</span>
              </label>
              <label className="option">
                <input type="radio" name="A13" value="1" onChange={handleChange} checked={formData.A13 === "1"} />
                <span>Ideas are captured, but action is rarely taken</span>
              </label>
              <label className="option">
                <input type="radio" name="A13" value="2" onChange={handleChange} checked={formData.A13 === "2"} />
                <span>Ideas are managed by innovation champions</span>
              </label>
              <label className="option">
                <input type="radio" name="A13" value="3" onChange={handleChange} checked={formData.A13 === "3"} />
                <span>Ideas are managed by innovation champions and experts through an idea management platform</span>
              </label>
              <label className="option">
                <input type="radio" name="A13" value="4" onChange={handleChange} checked={formData.A13 === "4"} />
                <span>Ideas are managed by an innovation organizational unit and include ideation, implementation and quality systems</span>
              </label>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Strategy (4/6)",
      content: (
        <div className="question-section">
          <div className="question-group">
            <div className="question">How well do the ideas align to your company's strategy?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A14" value="0" onChange={handleChange} checked={formData.A14 === "0"} />
                <span>We don't capture ideas</span>
              </label>
              <label className="option">
                <input type="radio" name="A14" value="1" onChange={handleChange} checked={formData.A14 === "1"} />
                <span>Ideas are generated, but not aligned to strategy</span>
              </label>
              <label className="option">
                <input type="radio" name="A14" value="2" onChange={handleChange} checked={formData.A14 === "2"} />
                <span>Ideas are aligned to departmental strategy</span>
              </label>
              <label className="option">
                <input type="radio" name="A14" value="3" onChange={handleChange} checked={formData.A14 === "3"} />
                <span>Ideas are well aligned to corporate strategy</span>
              </label>
              <label className="option">
                <input type="radio" name="A14" value="4" onChange={handleChange} checked={formData.A14 === "4"} />
                <span>Corporate strategy is driven by ideas</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">Which metrics do you monitor to analyze your innovation?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A15" value="0" onChange={handleChange} checked={formData.A15 === "0"} />
                <span>We don't monitor any innovation metrics</span>
              </label>
              <label className="option">
                <input type="radio" name="A15" value="1" onChange={handleChange} checked={formData.A15 === "1"} />
                <span>Ideation activity based (number of ideas, reward volumes, number of campaigns, etc.)</span>
              </label>
              <label className="option">
                <input type="radio" name="A15" value="2" onChange={handleChange} checked={formData.A15 === "2"} />
                <span>Investment and productization based (percentage of successful ideas, number of products launched, percentage of capital invested in innovation, etc.)</span>
              </label>
              <label className="option">
                <input type="radio" name="A15" value="3" onChange={handleChange} checked={formData.A15 === "3"} />
                <span>Goals and performance based (percentage of radical and disruptive initiatives in total, percentage of employees with performance goals related to innovation, average time from idea submission to monetization, etc.)</span>
              </label>
              <label className="option">
                <input type="radio" name="A15" value="4" onChange={handleChange} checked={formData.A15 === "4"} />
                <span>ROI and impact based (percentage of revenue from innovation initiatives, change in company value or market share as result of innovation, frequency of business model re-evaluations, etc.)</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">Who leads innovation in your company?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A16" value="0" onChange={handleChange} checked={formData.A16 === "0"} />
                <span>Nobody</span>
              </label>
              <label className="option">
                <input type="radio" name="A16" value="1" onChange={handleChange} checked={formData.A16 === "1"} />
                <span>Innovation champions and ambassadors</span>
              </label>
              <label className="option">
                <input type="radio" name="A16" value="2" onChange={handleChange} checked={formData.A16 === "2"} />
                <span>A sponsor from the top management</span>
              </label>
              <label className="option">
                <input type="radio" name="A16" value="3" onChange={handleChange} checked={formData.A16 === "3"} />
                <span>Dedicated Chief innovation officer or similar role</span>
              </label>
              <label className="option">
                <input type="radio" name="A16" value="4" onChange={handleChange} checked={formData.A16 === "4"} />
                <span>All C levels are leading innovation</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">Who advocates and communicates about innovation?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A17" value="0" onChange={handleChange} checked={formData.A17 === "0"} />
                <span>Nobody</span>
              </label>
              <label className="option">
                <input type="radio" name="A17" value="1" onChange={handleChange} checked={formData.A17 === "1"} />
                <span>Individuals with interest in innovation</span>
              </label>
              <label className="option">
                <input type="radio" name="A17" value="2" onChange={handleChange} checked={formData.A17 === "2"} />
                <span>Middle management</span>
              </label>
              <label className="option">
                <input type="radio" name="A17" value="3" onChange={handleChange} checked={formData.A17 === "3"} />
                <span>Senior leadership</span>
              </label>
              <label className="option">
                <input type="radio" name="A17" value="4" onChange={handleChange} checked={formData.A17 === "4"} />
                <span>Everybody</span>
              </label>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "About You (5/6)",
      content: (
        <div className="question-section">
          <div className="question-group">
            <div className="question">Where is your role positioned in the company hierarchy?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A18" value="Operational (no direct reports)" onChange={handleChange} checked={formData.A18 === "Operational (no direct reports)"} />
                <span>Operational (no direct reports)</span>
              </label>
              <label className="option">
                <input type="radio" name="A18" value="Team leader (I manage a small number of direct reports)" onChange={handleChange} checked={formData.A18 === "Team leader (I manage a small number of direct reports)"} />
                <span>Team leader (I manage a small number of direct reports)</span>
              </label>
              <label className="option">
                <input type="radio" name="A18" value="Middle management (I manage a small number of teams)" onChange={handleChange} checked={formData.A18 === "Middle management (I manage a small number of teams)"} />
                <span>Middle management (I manage a small number of teams)</span>
              </label>
              <label className="option">
                <input type="radio" name="A18" value="Senior management (I manage a small number of departments)" onChange={handleChange} checked={formData.A18 === "Senior management (I manage a small number of departments)"} />
                <span>Senior management (I manage a small number of departments)</span>
              </label>
              <label className="option">
                <input type="radio" name="A18" value="C level" onChange={handleChange} checked={formData.A18 === "C level"} />
                <span>C level</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">How important do you think innovation should be for your company?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A19" value="Not important at all, we're doing fine now" onChange={handleChange} checked={formData.A19 === "Not important at all, we're doing fine now"} />
                <span>Not important at all, we're doing fine now</span>
              </label>
              <label className="option">
                <input type="radio" name="A19" value="It's nice to have, but I don't think we should spend efforts on it" onChange={handleChange} checked={formData.A19 === "It's nice to have, but I don't think we should spend efforts on it"} />
                <span>It's nice to have, but I don't think we should spend efforts on it</span>
              </label>
              <label className="option">
                <input type="radio" name="A19" value="It would help us and we should spend a bit of time on it" onChange={handleChange} checked={formData.A19 === "It would help us and we should spend a bit of time on it"} />
                <span>It would help us and we should spend a bit of time on it</span>
              </label>
              <label className="option">
                <input type="radio" name="A19" value="It's very important, and it should be one of the top priorities" onChange={handleChange} checked={formData.A19 === "It's very important, and it should be one of the top priorities"} />
                <span>It's very important, and it should be one of the top priorities</span>
              </label>
              <label className="option">
                <input type="radio" name="A19" value="It is critical and we should dedicate resources and investments to make it work" onChange={handleChange} checked={formData.A19 === "It is critical and we should dedicate resources and investments to make it work"} />
                <span>It is critical and we should dedicate resources and investments to make it work</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">How interested and willing would you be to participate in innovation?</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A20" value="Not at all" onChange={handleChange} checked={formData.A20 === "Not at all"} />
                <span>Not at all</span>
              </label>
              <label className="option">
                <input type="radio" name="A20" value="I will participate if I must, but I'm not interested in innovation" onChange={handleChange} checked={formData.A20 === "I will participate if I must, but I'm not interested in innovation"} />
                <span>I will participate if I must, but I'm not interested in innovation</span>
              </label>
              <label className="option">
                <input type="radio" name="A20" value="I'm interested and willing to participate" onChange={handleChange} checked={formData.A20 === "I'm interested and willing to participate"} />
                <span>I'm interested and willing to participate</span>
              </label>
              <label className="option">
                <input type="radio" name="A20" value="I'm very interested and willing to be one of the innovation ambassadors" onChange={handleChange} checked={formData.A20 === "I'm very interested and willing to be one of the innovation ambassadors"} />
                <span>I'm very interested and willing to be one of the innovation ambassadors</span>
              </label>
              <label className="option">
                <input type="radio" name="A20" value="I'm extremely interested and I want to be recognized as an innovation leader and champion of change" onChange={handleChange} checked={formData.A20 === "I'm extremely interested and I want to be recognized as an innovation leader and champion of change"} />
                <span>I'm extremely interested and I want to be recognized as an innovation leader and champion of change</span>
              </label>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "About the Company (6/6)",
      content: (
        <div className="question-section">
          <div className="question-group">
            <div className="question">Industry</div>
            <div className="options industry-options">
              {[
                "Agriculture, forestry and fishing",
                "Mining and quarrying",
                "Manufacturing",
                "Electricity, gas, steam and air conditioning supply",
                "Water supply, sewerage, waste management and remediation activities",
                "Construction",
                "Wholesale and retail trade, repair of motor vehicles and motorcycles",
                "Logistics, transportation and storage",
                "Accommodation and food service activities",
                "Information and communication",
                "Financial and insurance activities",
                "Real estate activities",
                "Professional, scientific and technical activities",
                "Administrative and support service activities",
                "Public administration and defence; compulsory social security",
                "Education",
                "Human health and social work activities",
                "Arts, entertainment and recreation",
                "Other"
              ].map((industry) => (
                <label className="option" key={industry}>
                  <input 
                    type="radio" 
                    name="A21" 
                    value={industry} 
                    onChange={handleChange} 
                    checked={formData.A21 === industry} 
                  />
                  <span>{industry}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">Number of employees</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A22" value="0-50" onChange={handleChange} checked={formData.A22 === "0-50"} />
                <span>0-50</span>
              </label>
              <label className="option">
                <input type="radio" name="A22" value="50-200" onChange={handleChange} checked={formData.A22 === "50-200"} />
                <span>50-200</span>
              </label>
              <label className="option">
                <input type="radio" name="A22" value="200-500" onChange={handleChange} checked={formData.A22 === "200-500"} />
                <span>200-500</span>
              </label>
              <label className="option">
                <input type="radio" name="A22" value="500-2000" onChange={handleChange} checked={formData.A22 === "500-2000"} />
                <span>500-2000</span>
              </label>
              <label className="option">
                <input type="radio" name="A22" value="2000+" onChange={handleChange} checked={formData.A22 === "2000+"} />
                <span>2000+</span>
              </label>
            </div>
          </div>
          
          <div className="question-group">
            <div className="question">Annual revenue (in M USD)</div>
            <div className="options">
              <label className="option">
                <input type="radio" name="A23" value="0-1" onChange={handleChange} checked={formData.A23 === "0-1"} />
                <span>0-1</span>
              </label>
              <label className="option">
                <input type="radio" name="A23" value="1-10" onChange={handleChange} checked={formData.A23 === "1-10"} />
                <span>1-10</span>
              </label>
              <label className="option">
                <input type="radio" name="A23" value="10-50" onChange={handleChange} checked={formData.A23 === "10-50"} />
                <span>10-50</span>
              </label>
              <label className="option">
                <input type="radio" name="A23" value="50-200" onChange={handleChange} checked={formData.A23 === "50-200"} />
                <span>50-200</span>
              </label>
              <label className="option">
                <input type="radio" name="A23" value="200+" onChange={handleChange} checked={formData.A23 === "200+"} />
                <span>200+</span>
              </label>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Comments and Suggestions",
      content: (
        <div className="question-section">
          <div className="question-group">
            <div className="question">Would you like to add any thoughts, feedback or other comments?</div>
            <textarea 
              name="A24" 
              rows="4" 
              placeholder="Your comments here..." 
              value={formData.A24 || ""} 
              onChange={handleChange}
              className="comments-textarea"
            />
          </div>
        </div>
      )
    },
    {
      title: "Thank you!",
      content: (
        <div className="thank-you-section">
          <p>Thank you for completing the Innovation Maturity Assessment. Your responses have been recorded.</p>
          <div 
            className="submit-button"
            onClick={handleSubmit}
          >
            Calculate Results
          </div>
        </div>
      )
    }
  ];

  // Format the chart data for Recharts
  const chartData = results ? [
    { name: 'Culture', value: results.culture },
    { name: 'Processes', value: results.processes },
    { name: 'Tools', value: results.tools },
    { name: 'Strategy', value: results.strategy }
  ] : [];

  // Define custom colors for different categories
  const categoryColors = {
    'Culture': '#FF6384',
    'Processes': '#36A2EB',
    'Tools': '#FFCE56',
    'Strategy': '#4BC0C0'
  };

  return (
    <div className="innovation-assessment-container">
      {!showResults ? (
        <>
          <div className="title-container">
            <h2 className="form-title">Innovation Maturity Assessment</h2>
            <div className="steps-bar">
              {steps.map((step, index) => (
                <div 
                  key={index} 
                  className={`step-number ${index === currentStep ? 'active' : index < currentStep ? 'completed' : ''}`}
                  onClick={() => setCurrentStep(index)}
                >
                  {index + 1}
                </div>
              ))}
            </div>
          </div>
          <div className="form-container">
            <h3 className="step-title">{steps[currentStep].title}</h3>
            <div className="form-content">
              {steps[currentStep].content}
              <div className="form-navigation">
                {currentStep > 0 && (
                  <div 
                    className="prev-button" 
                    onClick={prevStep}
                  >
                    Previous
                  </div>
                )}
                {currentStep < steps.length - 1 && (
                  <div 
                    className="next-button" 
                    onClick={nextStep}
                  >
                    Next
                  </div>
                )}
                {currentStep === steps.length - 1 && (
                  <div 
                    className="submit-button" 
                    onClick={handleSubmit}
                  >
                    Submit
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="results-container">
          <h2 className="results-title">Innovation Maturity Assessment Results</h2>
          <div className="overall-score">
            <div className="score-circle">
              <div className="score-value">{results.overall.toFixed(1)}%</div>
              <div className="score-label">Overall Score</div>
            </div>
          </div>
          
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                <Tooltip formatter={(value) => [`${value.toFixed(2)}%`, 'Score']} />
                <Legend />
                <Bar dataKey="value" name="Maturity Score" radius={[10, 10, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={categoryColors[entry.name]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className="category-scores">
            {chartData.map((category) => (
              <div className="category-score" key={category.name}>
                <div className="category-name" style={{ color: categoryColors[category.name] }}>
                  {category.name}
                </div>
                <div className="score-bar-container">
                  <div 
                    className="score-bar" 
                    style={{ 
                      width: `${category.value}%`, 
                      backgroundColor: categoryColors[category.name]
                    }}
                  />
                  <div className="score-percent">{category.value.toFixed(2)}%</div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="results-message">
            <div dangerouslySetInnerHTML={{ __html: results.message.replace(/\n/g, '<br />').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/#+\s(.*?)$/gm, '<h3>$1</h3>') }} />
          </div>
          
          <div className="action-buttons">
            <div 
              className="restart-button"
              onClick={() => {
                setShowResults(false);
                setCurrentStep(0);
                setFormData({});
              }}
            >
              Start Over
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        .innovation-assessment-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 20px;
          font-family: 'Roboto', sans-serif;
          color: #333;
        }
        
        .title-container {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .form-title {
          font-size: 32px;
          font-weight: 600;
          color: #333;
          margin-bottom: 20px;
        }
        
        .steps-bar {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-bottom: 30px;
        }
        
        .step-number {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #e0e0e0;
          line-height: 36px;
          text-align: center;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .step-number.active {
          background-color: #FF6384;
          color: white;
          transform: scale(1.1);
        }
        
        .step-number.completed {
          background-color: #4BC0C0;
          color: white;
        }
        
        .form-container {
          background-color: white;
          border-radius: 10px;
          padding: 30px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }
        
        .step-title {
          font-size: 24px;
          font-weight: 500;
          margin-bottom: 25px;
          color: #333;
          border-bottom: 2px solid #f0f0f0;
          padding-bottom: 10px;
        }
        
        .question-section {
          margin-bottom: 20px;
        }
        
        .question-group {
          margin-bottom: 40px;
        }
        
        .question {
          font-weight: 600;
          margin-bottom: 15px;
          font-size: 18px;
          color: #333;
        }
        
        .options {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        
        .industry-options {
          max-height: 300px;
          overflow-y: auto;
          padding-right: 10px;
        }
        
        .option {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 15px;
          border-radius: 8px;
          background-color: #f9f9f9;
          transition: all 0.2s ease;
        }
        
        .option:hover {
          background-color: #f0f0f0;
        }
        
        .option input[type="radio"] {
          margin-top: 3px;
        }
        
        .option span {
          font-size: 16px;
          line-height: 1.4;
        }
        
        .comments-textarea {
          width: 100%;
          padding: 15px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 16px;
          line-height: 1.5;
          resize: vertical;
          min-height: 120px;
        }
        
        .form-navigation {
          display: flex;
          justify-content: space-between;
          margin-top: 30px;
        }
        
        .prev-button, .next-button, .submit-button, .restart-button {
          padding: 12px 25px;
          border: none;
          border-radius: 30px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-block;
        }
        
        .prev-button {
          background-color: #f0f0f0;
          color: #333;
        }
        
        .prev-button:hover {
          background-color: #e0e0e0;
        }
        
        .next-button {
          background-color: #36A2EB;
          color: white;
        }
        
        .next-button:hover {
          background-color: #2d8fd6;
        }
        
        .submit-button {
          background-color: #4BC0C0;
          color: white;
        }
        
        .submit-button:hover {
          background-color: #3da6a6;
        }
        
        .restart-button {
          background-color: #FF6384;
          color: white;
          margin: 0 auto;
          display: block;
        }
        
        .restart-button:hover {
          background-color: #e55a79;
        }
        
        .thank-you-section {
          text-align: center;
          font-size: 18px;
          line-height: 1.6;
        }
        
        /* Results styling */
        .results-container {
          background-color: white;
          border-radius: 10px;
          padding: 30px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }
        
        .results-title {
          font-size: 32px;
          font-weight: 600;
          text-align: center;
          margin-bottom: 30px;
          color: #333;
        }
        
        .overall-score {
          text-align: center;
          margin-bottom: 40px;
        }
        
        .score-circle {
          display: inline-flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 150px;
          height: 150px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FF6384, #36A2EB);
          color: white;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }
        
        .score-value {
          font-size: 36px;
          font-weight: 700;
        }
        
        .score-label {
          font-size: 16px;
          margin-top: 5px;
        }
        
        .chart-container {
          margin-bottom: 40px;
          height: 400px;
        }
        
        .category-scores {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 40px;
        }
        
        .category-score {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        
        .category-name {
          width: 100px;
          font-weight: 600;
          font-size: 16px;
        }
        
        .score-bar-container {
          flex-grow: 1;
          height: 30px;
          background-color: #f0f0f0;
          border-radius: 15px;
          overflow: hidden;
          position: relative;
        }
        
        .score-bar {
          height: 100%;
          border-radius: 15px;
          transition: width 1s ease;
        }
        
        .score-percent {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          font-weight: 600;
          color: #333;
        }
        
        .results-message {
          background-color: #f9f9f9;
          padding: 25px;
          border-radius: 10px;
          margin-bottom: 30px;
          line-height: 1.6;
        }
        
        .results-message h3 {
          color: #333;
          margin-top: 20px;
          margin-bottom: 10px;
          font-size: 20px;
        }
        
        .results-message strong {
          color: #333;
        }
        
        .action-buttons {
          margin-top: 30px;
          display: flex;
          justify-content: center;
        }
        
        .welcome-content {
          font-size: 16px;
          line-height: 1.6;
        }
        
        .welcome-content p {
          margin-bottom: 15px;
        }
        
        .welcome-content strong {
          color: #FF6384;
        }
        
        @media (max-width: 768px) {
          .steps-bar {
            flex-wrap: wrap;
          }
          
          .form-container {
            padding: 20px;
          }
          
          .option span {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default InnovationAssessment;